import { Injectable, signal, computed } from '@angular/core';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { OlLayerService } from './ol/ol-layer.service';
import { Fill, Stroke, Style } from 'ol/style';
import Select from 'ol/interaction/Select';
import { click } from 'ol/events/condition';
import Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import { MapService } from './map.service';
import CircleStyle from 'ol/style/Circle';


@Injectable({
  providedIn: 'root'
})
export class FeatureService {

  /* SIGNALS FEATURE INFO */
  private _currentFeatureInfo = signal<any[]>([]);

  readonly currentFeatureInfo = this._currentFeatureInfo.asReadonly();

  readonly featureCount = computed(() => {
    const features = this._currentFeatureInfo();
    if (!features || features.length === 0) return 0;

    let count = 0;
    features.forEach((feature: any) => {
      if (feature?.featureGroup?.length) {
        count += feature.featureGroup.length;
      }
    });

    return count;
  });

  /* OPENLAYERS STATE */
  private marker: Feature = new Feature();
  private featureInfoTooltip: Overlay;
  private hoverFeature: Feature | null = null;
  private selectedFeature: Feature | null = null;


  constructor(
    private mapService: MapService,
    private olLayerService: OlLayerService,
  ) { }

  /* FEATURE INFO METHODS */
  getCurrentFeatureInfo() {
    return this._currentFeatureInfo();
  }

  setCurrentFeatureInfo(featuresInfo: any[]) {
    const normalized = (featuresInfo || []).flat(2);
    this._currentFeatureInfo.set(normalized);
  }

  pushToCurrentFeatureInfo(feature: any) {
    this._currentFeatureInfo.update(prev => [...prev, feature]);
  }

  clearFeatureInfo() {
    this._currentFeatureInfo.set([]);
  }

  /* TOOLTIP */

  updateFeatureInfoTooltip(coordinates: number[]) {
    if (!this.featureInfoTooltip) return;

    const element = this.featureInfoTooltip.getElement() as HTMLElement;
    const count = this.featureCount();

    element.innerText =
      count > 0
        ? `${count} élément(s) trouvé(s)`
        : 'Aucun élément trouvé';

    element.style.display = 'block';

    this.featureInfoTooltip.setPosition(coordinates);
  }

  addFeatureInfoTooltip(map: Map) {
    const htmlTooltip: HTMLElement = document.getElementById('featureInfoTooltip');


    this.featureInfoTooltip = new Overlay({
      element: htmlTooltip,
      positioning: 'bottom-center',
      offset: [0, -10],
      stopEvent: false
    });


    map.addOverlay(this.featureInfoTooltip);
  }

  hideTooltip() {
    if (!this.featureInfoTooltip) return;

    const element = this.featureInfoTooltip.getElement() as HTMLElement;

    element.style.display = 'none';
    this.featureInfoTooltip.setPosition(undefined);
  }

  /* MARKER */

  addMarker(coordinates: number[], markerStyle: Style) {
    const markerPoint = new Point(coordinates);

    this.marker.setGeometry(markerPoint);
    this.marker.setStyle(markerStyle);

    const vectorLayer: any = this.olLayerService.getLayerById("drawLayer");

    if (!vectorLayer.getSource().hasFeature(this.marker)) {
      vectorLayer.getSource().addFeature(this.marker);
    }
  }

  clearMarker() {
    const vectorLayer: any = this.olLayerService.getLayerById("drawLayer");

    if (vectorLayer.getSource().hasFeature(this.marker)) {
      vectorLayer.getSource().removeFeature(this.marker);
    }
  }

  highlightFeatureHover(geometry: any) {
    const vectorLayer: any = this.olLayerService.getLayerById("drawLayer");

    if (this.hoverFeature) {
      vectorLayer.getSource().removeFeature(this.hoverFeature);
    }

    const feature = new Feature({ geometry });
    feature.setStyle(this.highlightHoveredStyle);

    vectorLayer.getSource().addFeature(feature);

    this.hoverFeature = feature;
  }

  clearSelected() {
    const vectorLayer: any = this.olLayerService.getLayerById("drawLayer");

    if (this.selectedFeature) {
      vectorLayer.getSource().removeFeature(this.selectedFeature);
      this.selectedFeature = null;
    }
  }

  highlightFeatureSelected(geometry: any) {
    const vectorLayer: any = this.olLayerService.getLayerById("drawLayer");

    if (this.selectedFeature) {
      vectorLayer.getSource().removeFeature(this.selectedFeature);
    }

    const feature = new Feature({ geometry });
    feature.setStyle(this.highlightStyle);

    vectorLayer.getSource().addFeature(feature);

    this.selectedFeature = feature;
  }

  clearHover() {
    const vectorLayer: any = this.olLayerService.getLayerById("drawLayer");

    if (this.hoverFeature) {
      vectorLayer.getSource().removeFeature(this.hoverFeature);
      this.hoverFeature = null;
    }
  }

  /* INTERACTIONS */


  addSelect(map: Map) {
    const select = new Select({ condition: click });
    map.addInteraction(select);
  }

  zoomToFeature(geom: any) {
    if (!geom) return;

    const map = this.mapService.getCurrentMap();

    map.getView().fit(geom.getExtent(), {
      padding: [50, 50, 50, 50],
      duration: 300,
      maxZoom: 18
    });

    this.highlightFeatureSelected(geom);
  }


  private highlightStyle = new Style({
    stroke: new Stroke({
      color: '#fffb00',
      width: 3
    }),
    fill: new Fill({
      color: '#fffb00b9'
    }),
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#fffb00' }),
      stroke: new Stroke({ color: '#fff', width: 2 })
    })
  });

  private highlightHoveredStyle = new Style({
    stroke: new Stroke({
      color: '#ff0000',
      width: 3
    }),
    fill: new Fill({
      color: '#ff00009c'
    }),
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#ff0000' }),
      stroke: new Stroke({ color: '#fff', width: 2 })
    })
  });

}
